import { motion, AnimatePresence } from 'framer-motion';
import { Plug, Wrench, BookOpen, Compass } from 'lucide-react';

export function RecommenderCard({ recs }) {
  const empty = !recs?.score;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.25 }}
      className="panel p-5"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="section-title">
          <Compass className="h-4 w-4 text-accent-cyan" />
          Recommended capabilities
        </div>
        {!empty && <span className="chip-cyan">match score · {recs.score}</span>}
      </div>

      <AnimatePresence mode="popLayout">
        {empty ? (
          <motion.p
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="rounded-xl border border-dashed border-border-subtle p-4 text-center text-xs text-ink-dim"
          >
            Describe a task above and we'll surface relevant MCP servers, skills, and tools.
          </motion.p>
        ) : (
          <motion.div
            key="filled"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid gap-3 md:grid-cols-3"
          >
            <Group icon={Plug} title="MCP servers" tone="cyan" items={recs.mcp} />
            <Group icon={BookOpen} title="Skills" tone="orange" items={recs.skills} />
            <Group icon={Wrench} title="Tools" tone="muted" items={recs.tools} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

function Group({ icon: Icon, title, items, tone }) {
  const toneCls =
    tone === 'cyan' ? 'text-accent-cyan-light border-accent-cyan/30 bg-accent-cyan/5' :
    tone === 'orange' ? 'text-accent-orange-soft border-accent-orange/30 bg-accent-orange/5' :
    'text-ink-muted border-border-subtle bg-bg-surface/50';

  return (
    <div className={`rounded-xl border p-3 ${toneCls}`}>
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold">
        <Icon className="h-3.5 w-3.5" />
        {title}
        <span className="font-mono text-[10px] opacity-60">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="font-mono text-[10px] text-ink-dim">no matches</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <motion.li
              key={it.k}
              whileHover={{ x: 2 }}
              className="flex items-center justify-between gap-2 rounded-lg bg-bg-deep/40 px-2 py-1.5"
            >
              <span className="truncate font-mono text-[11px] text-ink-primary">{it.k}</span>
              <span className="font-mono text-[9px] text-ink-dim">×{it.weight}</span>
            </motion.li>
          ))}
        </ul>
      )}
    </div>
  );
}
